import * as React from "react"
import { cn } from "@/lib/utils"
import { Button, buttonVariants } from "@/components/ui/button"
import { type VariantProps } from "class-variance-authority"

export interface ButtonTemplateAction {
  id: string
  label: string
  value?: string
  disabled?: boolean
  variant?: VariantProps<typeof buttonVariants>["variant"]
  icon?: React.ReactNode
}

export interface ButtonTemplateProps
  extends React.HTMLAttributes<HTMLDivElement> {
  title?: string
  description?: string
  actions: ButtonTemplateAction[]
  onActionClick: (action: ButtonTemplateAction) => void
  layout?: "grid" | "vertical" | "horizontal"
  maxColumns?: 2 | 3 | 4
  variant?: VariantProps<typeof buttonVariants>["variant"]
}

const ButtonTemplateInner = React.forwardRef<HTMLDivElement, ButtonTemplateProps>(
  (
    {
      className,
      title,
      description,
      actions,
      onActionClick,
      layout = "vertical",
      maxColumns = 2,
      variant = "outline",
      ...props
    },
    ref
  ) => {
    const handleActionClick = React.useCallback(
      (action: ButtonTemplateAction) => {
        if (!action.disabled) {
          onActionClick(action)
        }
      },
      [onActionClick]
    )

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent, action: ButtonTemplateAction) => {
        if ((e.key === "Enter" || e.key === " ") && !action.disabled) {
          e.preventDefault()
          onActionClick(action)
        }
      },
      [onActionClick]
    )

    if (actions.length === 0) {
      return null
    }

    const gridColumns = {
      2: "grid-cols-2",
      3: "grid-cols-3",
      4: "grid-cols-4",
    }

    const layoutClasses = {
      grid: `grid ${gridColumns[maxColumns]} gap-2`,
      vertical: "flex flex-col gap-2",
      horizontal: "flex flex-wrap gap-2",
    }

    return (
      <div
        ref={ref}
        className={cn(
          "rounded-lg border border-border bg-card p-4 shadow-sm",
          className
        )}
        role="group"
        aria-label={title || "Button template"}
        {...props}
      >
        {(title || description) && (
          <div className="mb-4 space-y-1">
            {title && (
              <h3 className="font-semibold text-sm text-foreground">
                {title}
              </h3>
            )}
            {description && (
              <p className="text-xs text-muted-foreground">{description}</p>
            )}
          </div>
        )}

        <div className={cn(layoutClasses[layout])}>
          {actions.map((action) => (
            <Button
              key={action.id}
              variant={action.variant || variant}
              className={cn(
                "w-full justify-center",
                layout === "horizontal" && "flex-1 min-w-[120px]"
              )}
              onClick={() => handleActionClick(action)}
              onKeyDown={(e) => handleKeyDown(e, action)}
              disabled={action.disabled}
              aria-label={action.label}
            >
              {action.icon && (
                <span className="mr-2 flex-shrink-0">{action.icon}</span>
              )}
              <span className="truncate">{action.label}</span>
            </Button>
          ))}
        </div>
      </div>
    )
  }
)

ButtonTemplateInner.displayName = "ButtonTemplateInner"

// Memoize to prevent re-renders when parent re-renders (e.g., when user types in input)
const ButtonTemplate = React.memo(ButtonTemplateInner)
ButtonTemplate.displayName = "ButtonTemplate"

export { ButtonTemplate }
